if (!String.prototype.includes) {
  String.prototype.includes = function(search, start) {
    const offset = typeof start === "number" ? start : 0;

    if (offset + search.length > this.length) {
      return false;
    }

    return this.indexOf(search, offset) !== -1;
  };
}

const SERIES_LABELS = {
  fortress: "Fortress Fund¹",
  "70-30": "70/30 Portfolio²",
  msci: "MSCI ACWI Global Equit³",
  bond: "US Aggregate Bond⁴",
  Fortress: "Fortress Fund¹"
};
const DRAWDOWN_SERIES = [
  { name: "Fortress", oldKey: "Fortress", codaKey: "Fortress" },
  { name: "70-30", oldKey: "70-30", codaKey: "70/30" },
  { name: "msci", oldKey: "msci", codaKey: "MSCI ACWI NTR" },
  { name: "bond", oldKey: "bond", codaKey: "US BOND AGGREGATE" }
];

export function PerformanceChart(container, options) {
  this.container = container;
  this.ratesManager = options.ratesManager;
  this.portfoliosConfig = options.portfoliosConfig;
  this.colors = options.colors;
}

PerformanceChart.prototype.init = function() {
  if (!this.container) return;

  this.chart = Highcharts.chart(this.container, {
    title: "",
    chart: {
      type: "areaspline"
    },
    xAxis: {
      labels: {
        formatter: categoryAxisLabel
      },
      tickPositioner: spreadTicksToLastCategory
    },
    legend: {
      layout: "horizontal",
      align: "left",
      useHTML: true,
      verticalAlign: "top",
      symbolPadding: 0,
      symbolWidth: 0,
      symbolRadius: 0,
      labelFormatter: function() {
        return checkedLegendLabel(this.name, this.color, true);
      }
    },
    yAxis: {
      title: false,
      type: "logarithmic",
      minorTickInterval: 0.1,
      labels: {
        formatter: moneyAxisLabel
      }
    },
    tooltip: {
      shared: true,
      valuePrefix: "$",
      valueDecimals: 0
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.5,
        marker: {
          enabled: false
        }
      }
    }
  });
};

function spreadTicksToLastCategory() {
  const tickPositions = this.tickPositions ? this.tickPositions.slice() : [];
  const categories = this.categories || [];
  const lastPosition = categories.length - 1;
  const visibleTickPositions = tickPositions.filter(function(position) {
    return position >= 0 && position <= lastPosition;
  });

  if (lastPosition < 0 || visibleTickPositions.indexOf(lastPosition) !== -1) {
    this.categoryTickLabels = visibleTickPositions.map(function(position) {
      return categories[position];
    });

    return visibleTickPositions;
  }

  if (visibleTickPositions.length === 0) {
    this.categoryTickLabels = [categories[lastPosition]];
    return [lastPosition];
  }

  this.categoryTickLabels = visibleTickPositions.slice(1).map(function(position) {
    return categories[position];
  });
  this.categoryTickLabels.push(categories[lastPosition]);

  return evenlySpreadTicks(this.categoryTickLabels.length, lastPosition);
}

function evenlySpreadTicks(tickCount, lastPosition) {
  if (tickCount <= 1) {
    return [lastPosition];
  }

  const interval = lastPosition / (tickCount - 1);

  return Array.from({ length: tickCount }, function(_, index) {
    if (index === tickCount - 1) {
      return lastPosition;
    }

    return index * interval;
  });
}

function categoryAxisLabel() {
  const tickIndex = this.axis.tickPositions.indexOf(this.pos);

  if (this.axis.categoryTickLabels && tickIndex !== -1) {
    return this.axis.categoryTickLabels[tickIndex];
  }

  return this.value;
}

PerformanceChart.prototype.generateAxisCategories = function(rates, dateFormat) {
  const format = dateFormat || "MMM YY";

  return rates.map(function(rate) {
    return dateFns.format(new Date(rate.Date), format);
  });
};

PerformanceChart.prototype.appendInitialCharts = function(
  dateBoundary,
  name,
  customChartOptions
) {
  if (!this.chart) return;

  const chartOptionsOverride = customChartOptions || {};
  const boundary = dateBoundary || "12/01/1987";

  this.portfoliosConfig.forEach(function(portfolio) {
    if (name && !portfolio.name.includes(name)) return;

    const weights = {};
    weights[portfolio.weight_name] = 100;
    const result = this.ratesManager.calculate(weights, boundary);
    const chartOptions = {
      name: portfolio.name,
      data: result.chart,
      color: this.colorForPortfolio(portfolio.name)
    };

    this.chart.addSeries(chartOptions);
    this.updateValueAxis(result.rates, chartOptionsOverride.dateFormat);

    if (customChartOptions) {
      this.chart.update(customChartOptions);
    }

    this.chart.redraw();
  }.bind(this));

  this.removeSymbols();
};

PerformanceChart.prototype.appendFortressReturnsChart = function(
  startDate,
  customChartOptions
) {
  if (!this.chart) return;

  ["fortress", "70-30", "msci", "bond"].forEach(function(seriesName) {
    const seriesDates = [];
    const seriesData = [];

    this.ratesManager.fortressReturnRates.forEach(function(entry) {
      if (dateFns.compareAsc(new Date(entry.date), new Date(startDate)) >= 0) {
        seriesDates.push(dateFns.format(new Date(entry.date), "DD MMM YY"));
        seriesData.push(entry[seriesName]);
      }
    });

    this.chart.addSeries({
      name: SERIES_LABELS[seriesName],
      data: seriesData,
      color: this.colorForSeries(seriesName)
    });

    this.chart.update({
      xAxis: {
        categories: seriesDates
      }
    });

    if (customChartOptions) {
      this.chart.update(customChartOptions);
    }

    this.chart.redraw();
    this.chart.reflow();
  }.bind(this));

  this.removeSymbols();
};

PerformanceChart.prototype.appendDrawdownChart = function(startDate, customChartOptions) {
  if (!this.chart) return;

  this.chart.update({
    yAxis: {
      type: "linear",
      labels: {
        formatter: function() {
          return this.value + " %";
        }
      }
    },
    tooltip: {
      shared: true,
      valueSuffix: " %",
      valuePrefix: "",
      valueDecimals: 2
    }
  });

  DRAWDOWN_SERIES.forEach(function(seriesConfig) {
    const seriesDates = [];
    const seriesData = [];

    this.ratesManager.drawdownRates.forEach(function(entry) {
      const date = drawdownEntryDate(entry);
      const value = drawdownEntryValue(entry, seriesConfig);

      if (date && value !== null && date > new Date(startDate)) {
        seriesDates.push(dateFns.format(date, "MMM-YY"));
        seriesData.push(value);
      }
    });

    this.chart.addSeries({
      name: SERIES_LABELS[seriesConfig.name],
      data: seriesData,
      color: this.colorForSeries(seriesConfig.name)
    });

    this.chart.update({
      xAxis: {
        categories: seriesDates
      }
    });

    if (customChartOptions) {
      this.chart.update(customChartOptions);
    }

    this.chart.redraw();
  }.bind(this));

  this.removeSymbols();
};

function drawdownEntryDate(entry) {
  const value = entry.Date || entry.Item;
  const date = new Date(value);

  return isNaN(date.getTime()) ? null : date;
}

function drawdownEntryValue(entry, seriesConfig) {
  if (isCodaDrawdownEntry(entry)) {
    const value = Number(entry[seriesConfig.codaKey]);

    return Number.isNaN(value) ? null : value;
  }

  if (Object.prototype.hasOwnProperty.call(entry, seriesConfig.oldKey)) {
    const value = Number(entry[seriesConfig.oldKey]);

    return Number.isNaN(value) ? null : parseFloat((value * 100).toFixed(2));
  }

  return null;
}

function isCodaDrawdownEntry(entry) {
  return Object.prototype.hasOwnProperty.call(entry, "Date");
}

PerformanceChart.prototype.updateValueAxis = function(rates, dateFormat) {
  const values = rates.reduce(function(allValues, entry) {
    return allValues.concat(
      Object.values(entry).filter(function(value) {
        return typeof value !== "string";
      })
    );
  }, []);

  this.chart.update({
    xAxis: {
      categories: this.generateAxisCategories(rates, dateFormat)
    },
    yAxis: {
      title: false,
      type: "linear",
      min: Math.min.apply(null, values),
      max: Math.max.apply(null, values),
      labels: {
        formatter: moneyAxisLabel
      }
    }
  });
};

PerformanceChart.prototype.removeSymbols = function() {
  if (!this.chart) return;

  $(this.chart.series).each(function(index, serie) {
    if (serie.legendSymbol) serie.legendSymbol.hide();
    if (serie.legendLine) serie.legendLine.hide();
  });
};

PerformanceChart.prototype.colorForPortfolio = function(name) {
  if (name.includes("Fortress")) return this.colors.fortress;
  if (name.includes("US Aggregate")) return this.colors.bond;
  if (name.includes("MSCI")) return this.colors.msci;
  if (name.includes("70-30")) return this.colors.seventy;
  return undefined;
};

PerformanceChart.prototype.colorForSeries = function(name) {
  if (name.includes("Fortress") || name.includes("fortress")) {
    return this.colors.fortress;
  }
  if (name.includes("70-30")) return this.colors.seventy;
  if (name.includes("msci")) return this.colors.msci;
  if (name.includes("bond")) return this.colors.bond;
  return undefined;
};

export function checkedLegendLabel(name, color, checked) {
  return (
    '<div class="legendnamecl" style="display: flex;">' +
    '<div style="background-color: ' +
    color +
    '; border-radius: 50%; width: 16px; height: 16px; margin: 0 5px;"></div>' +
    '<label class="legend-control legend-control-checkbox">' +
    '<input type="checkbox" ' +
    (checked ? "checked" : "") +
    " />" +
    '<div class="legend-control_indicator"></div>' +
    "</label>" +
    '<span class="nameclfortopalign" style="font: inherit; text-transform: uppercase; font-size: .8rem;">' +
    name +
    "</span>" +
    "</div>"
  );
}

function moneyAxisLabel() {
  const label = this.axis && this.axis.defaultLabelFormatter ?
    this.axis.defaultLabelFormatter.call(this) :
    this.value;

  return "$" + label;
}
